import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronUp } from "react-feather";
import {
  FaUserInjured,
  FaHandHoldingHeart,
  FaTint,
  FaIdCard,
} from "react-icons/fa";
import { SuccessBadge, ScoreBar, useMatchScore } from "./MatchSuccessScore";

const ThreeWayCycleDisplay = ({ cycles }) => {
  const [expandedCycle, setExpandedCycle] = useState(null);

  const toggleExpand = (index) => {
    setExpandedCycle(expandedCycle === index ? null : index);
  };

  return (
    <div className="container p-1 mx-auto md:p-4">
      <h1 className="mb-6 text-3xl font-bold text-center text-purple-700">
        3-Way Cycles Found: {cycles.length}
      </h1>
      <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg text-sm text-purple-800">
        <strong>How 3-Way Matching Works:</strong> A 3-way cycle involves 3
        incompatible pairs. R1 receives from D2, R2 receives from D3, and R3
        receives from D1 — forming a complete cycle based on blood group
        compatibility.
      </div>
      {cycles.map((cycle, index) => (
        <CycleCard
          key={index}
          cycle={cycle}
          index={index}
          isExpanded={expandedCycle === index}
          onToggle={() => toggleExpand(index)}
        />
      ))}
    </div>
  );
};

const ThreeWayScores = ({ cycle }) => {
  // Compute scores for all 3 sub-pairs
  const s1 = useMatchScore(cycle.pair1.recipient?._id, cycle.pair2.donor?._id);
  const s2 = useMatchScore(cycle.pair2.recipient?._id, cycle.pair3.donor?._id);
  const s3 = useMatchScore(cycle.pair3.recipient?._id, cycle.pair1.donor?._id);

  const exchangeResults = [s1, s2, s3];
  const scores = exchangeResults.map(r => r.compositeScore);
  const incompatible = s1.isIncompatible || s2.isIncompatible || s3.isIncompatible;
  const loading = s1.loading || s2.loading || s3.loading;

  // Overall = min of all 3 (all must work for the cycle to complete)
  const validScores = scores.filter(s => s !== null);
  const overall = validScores.length === 3
    ? Math.round(Math.min(...validScores))
    : validScores.length > 0
      ? Math.round(Math.min(...validScores))
      : null;

  // Find the weakest link
  const minIdx = overall !== null ? scores.indexOf(Math.min(...validScores)) : -1;

  const pairLabels = ["R1 ← D2", "R2 ← D3", "R3 ← D1"];
  const pairColors = ["bg-blue-50 border-blue-200", "bg-green-50 border-green-200", "bg-orange-50 border-orange-200"];

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-full">
        <div className="w-4 h-4 border-2 border-gray-400 border-t-gray-600 rounded-full animate-spin" />
        <span className="text-xs text-gray-500 font-medium">Analyzing cycle compatibility...</span>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Overall cycle score */}
      <div className="flex items-center gap-3">
        <SuccessBadge score={overall} size="md" isIncompatible={incompatible} />
        <div className="flex flex-col">
          <span className="text-sm font-semibold text-gray-800">
            Cycle Success Score
          </span>
          <span className="text-[10px] text-gray-500">
            {overall !== null && overall >= 80
              ? "All 3 exchanges are strong — cycle is viable"
              : overall !== null && overall >= 60
                ? "Cycle is likely viable — review weak exchanges below"
                : overall !== null
                  ? "One or more exchanges are high risk — consider alternatives"
                  : "Scoring incomplete"}
          </span>
        </div>
      </div>
      {overall !== null && <ScoreBar score={overall} className="w-full max-w-md" isIncompatible={incompatible} />}

      {/* Individual sub-pair scores */}
      <div className="grid grid-cols-1 gap-2 mt-3 md:grid-cols-3">            {scores.map((score, i) => {
              const subIncompatible = exchangeResults[i]?.isIncompatible || false;
              return (
              <div
                key={i}
                className={`p-2 rounded border ${pairColors[i]} ${
                  i === minIdx && overall !== null && overall < 60
                    ? "ring-2 ring-red-300"
                    : ""
                }`}
              >
                <p className="text-[10px] font-semibold text-gray-500 uppercase mb-1">
                  {pairLabels[i]}
                  {i === minIdx && overall !== null && overall < 60 && (
                    <span className="ml-1 text-red-500">⚠️ Weakest</span>
                  )}
                </p>
                <div className="flex items-center gap-1.5">
                  <SuccessBadge score={score} size="sm" showLabel={false} isIncompatible={subIncompatible} />
                  <div className="flex flex-col">
                    <span className="text-xs font-medium text-gray-700">
                      {score !== null ? `${Math.round(score)}%` : "—"}
                    </span>
                    <ScoreBar score={score} className="w-12" />
                  </div>
                </div>
              </div>
            );
          })}
          </div>
    </div>
  );
};

const CycleCard = ({ cycle, index, isExpanded, onToggle }) => {
  let navigate = useNavigate();

  return (
    <div
      className={`mb-6 overflow-hidden bg-white rounded-lg shadow-lg transition-all duration-300 hover:shadow-xl ${
        isExpanded ? "border-2 border-purple-300 bg-purple-50" : ""
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-50 to-indigo-50">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-semibold text-purple-700">
            Cycle {index + 1}
          </h2>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() =>
              navigate("/v1/deep-analyze-3way", { state: cycle })
            }
            className="px-3 py-1 text-sm text-white transition duration-300 bg-purple-500 rounded-md hover:bg-purple-600"
          >
            <i className="mdi mdi-stethoscope"></i>&nbsp; Deep Analyze
          </button>
          <button
            onClick={onToggle}
            className="p-1 text-gray-500 transition duration-300 rounded-full hover:bg-gray-200"
          >
            {isExpanded ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
          </button>
        </div>
      </div>

      {/* Cycle success score */}
      <div className="px-4 pb-2">
        <ThreeWayScores cycle={cycle} />
      </div>

      {/* 3 sub-pairs in a cycle flow */}
      <div className="p-4 pt-0">
        <div className="flex flex-col items-center justify-center gap-3 md:flex-row">
          <SubPairCard
            label="Pair 1"
            recipient={cycle.pair1.recipient}
            donor={cycle.pair2.donor}
            arrowLabel="← D2"
            bgColor="bg-blue-50"
          />
          <ArrowRightIcon />
          <SubPairCard
            label="Pair 2"
            recipient={cycle.pair2.recipient}
            donor={cycle.pair3.donor}
            arrowLabel="← D3"
            bgColor="bg-green-50"
          />
          <ArrowRightIcon />
          <SubPairCard
            label="Pair 3"
            recipient={cycle.pair3.recipient}
            donor={cycle.pair1.donor}
            arrowLabel="← D1"
            bgColor="bg-orange-50"
          />
        </div>
      </div>

      {/* Expanded details */}
      {isExpanded && (
        <div className="p-4 pt-0 space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <SubPairDetails
              title="Pair 1: R1 ← D2"
              recipient={cycle.pair1.recipient}
              donor={cycle.pair2.donor}
              bgColor="bg-blue-100"
            />
            <SubPairDetails
              title="Pair 2: R2 ← D3"
              recipient={cycle.pair2.recipient}
              donor={cycle.pair3.donor}
              bgColor="bg-green-100"
            />
            <SubPairDetails
              title="Pair 3: R3 ← D1"
              recipient={cycle.pair3.recipient}
              donor={cycle.pair1.donor}
              bgColor="bg-orange-100"
            />
          </div>
        </div>
      )}
    </div>
  );
};

const SubPairCard = ({ label, recipient, donor, arrowLabel, bgColor }) => {
  const getBloodGroup = (person) =>
    person?.blood_group?.label || person?.bloodgroup || "N/A";
  const getShortName = (person) => {
    if (person?.name) return person.name;
    const n =
      person?.donor_name || person?.recipient_name || person?.recipient_name || "";
    return n.length > 15 ? n.substring(0, 15) + "..." : n;
  };

  return (
    <div
      className={`${bgColor} rounded-lg p-3 shadow flex-1 w-full border border-gray-200`}
    >
      <p className="text-xs font-semibold text-gray-500 mb-1">{label}</p>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <FaUserInjured className="w-5 h-5 text-indigo-500" />
          <span className="text-sm font-medium text-gray-800">
            {getShortName(recipient)}
          </span>
        </div>
        <span className="text-xs font-bold text-purple-600">{arrowLabel}</span>
        <div className="flex items-center space-x-2">
          <FaHandHoldingHeart className="w-5 h-5 text-green-500" />
          <span className="text-sm font-medium text-gray-800">
            {getShortName(donor)}
          </span>
        </div>
      </div>
      <div className="flex justify-between mt-1 text-xs text-gray-500">
        <span>
          <FaTint className="inline mr-1 text-red-400" />
          {getBloodGroup(recipient)}
        </span>
        <span>
          <FaTint className="inline mr-1 text-red-400" />
          {getBloodGroup(donor)}
        </span>
      </div>
    </div>
  );
};

const SubPairDetails = ({ title, recipient, donor, bgColor }) => {
  const getBloodGroup = (person) =>
    person?.blood_group?.label || person?.bloodgroup || "N/A";
  const getFullName = (person) => {
    if (person?.name) return person.name;
    return `${person?.member_title || ""} ${
      person?.donor_name || person?.recipient_name || ""
    } ${person?.last_name || ""}`.trim();
  };
  const getCode = (person) =>
    person?.recipient_code || person?.donor_code || "N/A";

  return (
    <div className={`${bgColor} rounded-lg p-4 shadow-sm border border-gray-200`}>
      <h4 className="text-sm font-semibold text-gray-700 mb-3">{title}</h4>
      <div className="space-y-1 text-xs">
        <p>
          <strong>Recipient:</strong> {getFullName(recipient)} ({getCode(recipient)}){" "}
          <FaTint className="inline ml-1 text-red-400" /> {getBloodGroup(recipient)}
        </p>
        <p>
          <strong>Donor:</strong> {getFullName(donor)} ({getCode(donor)}){" "}
          <FaTint className="inline ml-1 text-red-400" /> {getBloodGroup(donor)}
        </p>
      </div>
    </div>
  );
};

const ArrowRightIcon = () => (
  <div className="flex items-center justify-center py-2 md:py-0">
    <svg
      className="w-6 h-6 text-purple-400"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13 7l5 5m0 0l-5 5m5-5H6"
      />
    </svg>
  </div>
);

export default ThreeWayCycleDisplay;
